import { Intent } from "@blueprintjs/core";
import classNames from "classnames";
import css from "./Surface.module.scss";

export type SurfaceTone = "none" | "tint" | "white";
export type SurfaceRadius = "none" | "md" | "lg";
export type SurfacePadding = "none" | "very-small" | "small" | "large";
interface SurfaceProps {
    /** Background fill. Defaults to the tinted (light-gray) surface. */
    readonly tone?: SurfaceTone;
    /** Corner rounding. */
    readonly radius?: SurfaceRadius;
    /** Add a 1px divider border. */
    readonly bordered?: boolean;
    /** Add a card drop shadow. */
    readonly elevated?: boolean;
    /** Add padding to the surface. */
    readonly padding?: SurfacePadding;
    /**
     * Tint the fill and border with an intent color, overriding `tone`. Also
     * exposes `--surface-accent` / `--surface-border` for descendants to match.
     */
    readonly intent?: Intent;
    /** Applied to the surface root for layout, padding, and sizing. */
    readonly className?: string;
    readonly children?: React.ReactNode;
}

/**
 * The shared boxed-surface primitive: a tinted-or-white panel with optional
 * rounding, border, and shadow. Owns only the box appearance — consumers add
 * their own layout/padding via `className`.
 */
export const Surface: React.FC<SurfaceProps> = ({
    tone = "tint",
    radius = "md",
    bordered = false,
    elevated = false,
    padding = "none",
    intent,
    className,
    children
}) => (
    <div
        className={classNames(
            css["Surface"],
            css[`Surface--tone-${tone}`],
            css[`Surface--radius-${radius}`],
            css[`Surface--padding-${padding}`],
            {
                [css["Surface--bordered"]]: bordered,
                [css["Surface--elevated"]]: elevated,
                [css[`Surface--intent-${intent}`]]:
                    intent !== undefined && intent !== Intent.NONE
            },
            className
        )}
    >
        {children}
    </div>
);
